/**
 * Loads the startup benchmark's reference and its four frames (spec §4.3).
 *
 * Injectable so it is testable in jsdom; `browserAssetDeps` is the real thing.
 * Never rejects — every failure is a reason, and the caller falls back to the
 * black-frame speed test with an `unknown` accuracy verdict.
 */
import {
  BENCHMARK_BASE_PATH,
  BENCHMARK_FRAME_COUNT,
  BENCHMARK_FRAME_HEIGHT,
  BENCHMARK_FRAME_WIDTH,
  validateReference,
  type BenchmarkReference,
} from "./benchmarkReference";

export interface AssetDeps<I extends { width: number; height: number }> {
  fetchJson: (url: string) => Promise<unknown>;
  loadImage: (url: string) => Promise<I>;
}

export type AssetsResult<I> =
  | { ok: true; assets: { reference: BenchmarkReference; images: I[] } }
  | { ok: false; reason: string };

export async function loadBenchmarkAssets<I extends { width: number; height: number }>(
  deps: AssetDeps<I>
): Promise<AssetsResult<I>> {
  let raw: unknown;
  try {
    raw = await deps.fetchJson(BENCHMARK_BASE_PATH + "reference.json");
  } catch (e) {
    return { ok: false, reason: "assets-unavailable" };
  }
  const check = validateReference(raw);
  if (!check.ok) return { ok: false, reason: check.reason };

  const images: I[] = [];
  for (let i = 0; i < BENCHMARK_FRAME_COUNT; i++) {
    let img: I;
    try {
      img = await deps.loadImage(BENCHMARK_BASE_PATH + check.reference.frames[i].file);
    } catch (e) {
      return { ok: false, reason: "assets-unavailable" };
    }
    if (img.width !== BENCHMARK_FRAME_WIDTH || img.height !== BENCHMARK_FRAME_HEIGHT) {
      return { ok: false, reason: "reference-invalid" };
    }
    images.push(img);
  }
  return { ok: true, assets: { reference: check.reference, images } };
}

/**
 * Same origin, so the canvas can read the pixels without CORS. Frames go
 * through `Image` (the service worker's `images` route); the reference through
 * `fetch` (its `ml-models` route, which caches `.json` cache-first — safe only
 * because the paths are versioned).
 */
export const browserAssetDeps: AssetDeps<HTMLImageElement> = {
  fetchJson: async (url) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error("HTTP " + res.status);
    return res.json();
  },
  loadImage: (url) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("benchmark frame failed to load: " + url));
      img.src = url;
    }),
};
